import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { permissionsApi } from '../services/permissionService';
import { Permission } from '../types/permission';

interface PermissionContextType {
  permissions: Permission | null;
  loading: boolean;
  error: string | null;
  hasPermission: (sectionId: string, pageId: string, actionId?: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

// إنشاء سياق الصلاحيات
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// مزود سياق الصلاحيات
export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<Permission | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // تحميل صلاحيات المستخدم
  const loadPermissions = async () => {
    if (!isAuthenticated || !user || !user._id) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await permissionsApi.getUserPermissions(user._id);
      
      if (response.hasPermissions) {
        setPermissions(response.permissions || null);
      } else {
        setPermissions(null);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('حدث خطأ أثناء تحميل الصلاحيات');
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  // تحميل الصلاحيات عند تغيير المستخدم
  useEffect(() => {
    loadPermissions();
  }, [user, isAuthenticated]);

  // التحقق من صلاحية محددة
  const hasPermission = (sectionId: string, pageId: string, actionId: string = 'view'): boolean => {
    // التحقق إذا كان المستخدم مدير نظام (تجاوز جميع الصلاحيات)
    if (user && user.isSystemAdmin) {
      return true;
    }
    
    // إذا كانت الصلاحيات في حالة تحميل، نعتبر أن المستخدم ليس لديه صلاحية حتى يتم التحميل
    if (loading) {
      return false;
    }
    
    // إذا لم يكن المستخدم مصادق عليه، ارجع false
    if (!isAuthenticated) {
      console.log('[PermissionContext] المستخدم غير مصادق عليه، إرجاع false');
      return false;
    }
    
    // إذا لم تكن هناك صلاحيات، ارجع false
    if (!permissions || !permissions.sections) {
      console.log('[PermissionContext] لا توجد صلاحيات للمستخدم، إرجاع false');
      return false;
    }

    // طباعة معلومات الصلاحيات المتوفرة للمستخدم للتشخيص
    if (permissions.sections) {
      const sectionsCount = permissions.sections.length;
      console.log(`[PermissionContext] عدد الأقسام المتاحة للمستخدم: ${sectionsCount}`);
      
      // فحص ما إذا كان القسم المطلوب موجود
      const hasSection = permissions.sections.some(s => s.identifier === sectionId);
      console.log(`[PermissionContext] هل القسم "${sectionId}" موجود؟ ${hasSection}`);
      
      // إذا كان القسم موجود، فحص ما إذا كانت الصفحة موجودة
      if (hasSection) {
        const section = permissions.sections.find(s => s.identifier === sectionId);
        const hasPage = section?.pages.some(p => p.identifier === pageId);
        console.log(`[PermissionContext] هل الصفحة "${pageId}" موجودة في القسم "${sectionId}"؟ ${hasPage}`);
      }
    }

    // التحقق من الصلاحية باستخدام الخدمة
    const result = permissionsApi.checkPermission(permissions, sectionId, pageId, actionId);
    console.log(`[PermissionContext] نتيجة التحقق من الصلاحية (${sectionId}, ${pageId}, ${actionId}): ${result}`);
    return result;
  };

  // إعادة تحميل الصلاحيات
  const refreshPermissions = async () => {
    await loadPermissions();
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        error,
        hasPermission,
        refreshPermissions
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

// دالة مساعدة لاستخدام سياق الصلاحيات
export const usePermissionsContext = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissionsContext must be used within a PermissionProvider');
  }
  return context;
};